/**
 * Is the app's own server there, and what can it do?
 *
 * Asked once when the app loads. The answer decides whether the assistant uses
 * the server's keys (everyone gets the AI and a natural voice with nothing to
 * set up) or falls back to whatever the family record says (the built-in
 * helper, or keys an administrator typed into a browser).
 *
 * Two kinds of "no", treated differently:
 *
 *   - **There is no server.** A plain static host has no `/api/assistant/status`
 *     and answers with the app's own page instead — anything that is not JSON.
 *     That is remembered, so nothing waits on asking again.
 *   - **It could not be reached.** A flat spot in the Wi-Fi, a timeout, a server
 *     mid-restart. That is forgotten, so the next question asks again — and a
 *     question asked while the first check is still out waits for it rather
 *     than being answered by the built-in helper in the browser's robot voice.
 */
import { ASSISTANT_API, type ServerStatus } from './providers/serverContract';

let asking: Promise<ServerStatus | null> | null = null;

export function detectServer(): Promise<ServerStatus | null> {
  if (asking) return asking;
  let reachable = false;
  const attempt = (async (): Promise<ServerStatus | null> => {
    if (typeof fetch === 'undefined') return null;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    try {
      const res = await fetch(`${ASSISTANT_API}/status`, { signal: controller.signal, headers: { accept: 'application/json' } });
      // A server error is a server that is there and struggling, not an absent one.
      if (res.status >= 500) return null;
      reachable = true;
      if (!res.ok || !(res.headers.get('content-type') ?? '').includes('application/json')) return null;
      const body = (await res.json()) as Partial<ServerStatus> | null;
      if (!body || !('answer' in body)) return null;
      return { answer: body.answer ?? null, listen: body.listen ?? null, speak: body.speak ?? null };
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  })();
  asking = attempt;
  void attempt.then((found) => {
    if (!found && !reachable && asking === attempt) asking = null;
  });
  return attempt;
}
